/**
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form as FormikForm, Field } from 'formik';

import TextInput from '../PropertyContactForm/TextInput/TextInput';
import Exit from 'components/Icons/Exit/Exit';
import Check from 'components/Icons/Check/Check';
import Spinner from 'components/Spinner/Spinner';

import { createValidator } from '../PropertyContactForm/validator';

import './DownloadReport.scss';

/**
 * This is the DownloadReport component.
 *
 * @author Andreas Høegh  <andreas.hoegh@apt.no>
 *
 * Pretty much a stright copy from PropertyContactForm
 *
 * @return {JSX}
 */
function DownloadReport({
  report,
  action,
  fields,
  submit,
  errorMessages,
  successMessage,
  failureMessage,
}) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(values, actions) {
    setLoading(true);
    setSuccess(false);

    const errors = await actions.validateForm();

    if (!Object.keys(errors).length) {
      try {
        const rawResponse = await fetch(action, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            ...values,
          }),
        });

        const response = await rawResponse.json();

        if (response.errors) {
          setLoading(false);
          actions.setErrors(response.errors);
        }

        if (response.error) {
          setLoading(false);
          actions.setErrors({
            server: failureMessage,
          });
        }

        if (response.status === false) {
          setLoading(false);
          actions.setErrors(response.errors);
        }

        if (response.status === true) {
          setLoading(false);
          setSuccess(true);
          actions.resetForm();
          window.location.href = report;
        }
      } catch (err) {
        console.log('err', err);
        actions.setErrors({
          server: failureMessage,
        });
        setLoading(false);
      }
    }
    actions.setSubmitting(false);
  }

  function parseInitialValues(fields) {
    const values = {};

    fields.forEach(field => {
      if (Array.isArray(field)) {
        field.forEach(field => {
          values[field.name] = field.value;
        });

        return;
      }

      values[field.name] = field.value;
    });

    return values;
  }

  function renderField(field, i) {
    if (Array.isArray(field)) {
      return (
        <div key={`field-group-${i}`} className="field-group">
          {field.map(renderField)}
        </div>
      );
    }

    if (field.type === 'hidden') {
      return <Field key={field.name} {...field} />;
    }

    if (field.type === 'plainText') {
      if (field.value !== null) {
        return (
          <a
            key={field.id}
            target="_blank"
            rel="noreferrer"
            className="privacy"
            href={field.value.url}
          >
            {field.value.title}
          </a>
        );
      }
    }

    return <Field key={field.name} {...field} component={TextInput} />;
  }

  return (
    <Formik
      initialValues={parseInitialValues(fields)}
      enableReinitialize
      validate={createValidator(errorMessages)}
      onSubmit={handleSubmit}
    >
      {({ touched, errors, isSubmitting }) => (
        <FormikForm className="download-marketing-report">
          {fields.map(renderField)}

          <button
            type="submit"
            className="submit-button button orange"
            disabled={
              !Object.values(touched).some(t => t) ||
              Object.keys(touched).some(key => touched[key] && errors[key]) ||
              isSubmitting
            }
          >
            {loading ? <Spinner /> : submit}
          </button>

          <div className="server-response">
            {errors.server && (
              <p className="error-message" aria-live="polite">
                <Exit title={'failure'} /> {errors.server}
              </p>
            )}
            {success && (
              <p className="success-message" aria-live="polite">
                <Check title={'success'} /> {successMessage}
              </p>
            )}
          </div>
        </FormikForm>
      )}
    </Formik>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
DownloadReport.propTypes = {
  report: PropTypes.string.isRequired,
  action: PropTypes.string.isRequired,
  fields: PropTypes.array,
  submit: PropTypes.string,
  errorMessages: PropTypes.object,
  successMessage: PropTypes.string,
  failureMessage: PropTypes.string,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
DownloadReport.defaultProps = {
  fields: [],
  submit: '',
  errorMessages: {},
  successMessage: 'Your message has been sent',
  failureMessage: 'An error occured, and your message was not sent!',
};

export default DownloadReport;
